(ns minorodata.components.pipeline-editor.context-menu
  (:require
   ["copy-to-clipboard" :as copy]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.sql-console.utils :refer [fetch-params generate-columns
                                                    generate-drop-table generate-select
                                                    generate-table-name]]
   [minorodata.sandbox.model :refer [->pipeline-transform-unsaved]]
   [re-posh.core :as re-posh]
   [ui.context-menu :refer [context-menu-item context-menu-label
                            context-menu-separator]]))

(defn- copy! [text]
  (copy text #js {:format "text/plain"}))

;; ---- shared helpers ----

(defn- fetch-children [params on-success]
  (dispatch [:sql-console.api/fetch-metadata params {:on-success on-success}]))

(defn- with-children [node f]
  (let [params   (fetch-params node)
        children @(re-posh/subscribe [:sql-metadata-partial/prop
                                      [:sql-metadata-partial/params params]
                                      :sql-metadata-partial/metadata])]
    (if children
      (f children)
      (fetch-children params f))))

(defn- insert-into-transform! [master-id sql-fragment]
  (let [unsaved   @(re-posh/subscribe [:pipeline-transform-unsaved/pull
                                       [:pipeline-transform-unsaved/id master-id]])
        transform @(re-posh/subscribe [:pipeline-editor.subs/full-transform master-id])
        current   (or (:pipeline-transform-unsaved/command unsaved)
                      (:sql transform)
                      (:command transform)
                      "")
        new-cmd   (str current "\n" sql-fragment)]
    (when-not unsaved
      (dispatch [:pipeline-transform-unsaved/add (->pipeline-transform-unsaved {:id master-id})]))
    (dispatch [:pipeline-editor.events/update-local-transform master-id new-cmd])))

;; ---- per-action handlers ----

(defn- copy-catalog-name [node]
  #(copy! (:table-cat node)))

(defn- copy-schema-name [node]
  #(copy! (:table-schem node)))

(defn- copy-column-name [node]
  #(copy! (:column-name node)))

(defn- copy-table-name [node]
  #(copy! (generate-table-name node)))

(defn- preview-handler [{:keys [type id group-id]}]
  #(dispatch [:app.events/navigate-query {:tab-preview-type     type
                                          :tab-preview-id       id
                                          :tab-preview-group-id group-id}]))

(defn- generate-select-handler [master-id node]
  #(with-children node
                  (fn [children]
                    (insert-into-transform!
                     master-id
                     (generate-select {:cols  (generate-columns children)
                                       :table (generate-table-name node)})))))

(defn- copy-select-handler [node]
  #(with-children node
                  (fn [children]
                    (copy! (generate-select {:cols  (generate-columns children)
                                             :table (generate-table-name node)})))))

(defn- generate-drop-handler [master-id node]
  #(insert-into-transform!
    master-id
    (generate-drop-table {:table (generate-table-name node)})))

(defn- copy-drop-handler [node]
  #(copy! (generate-drop-table {:table (generate-table-name node)})))

;; ---- public render fns ----

(defn node-context-menu
  "Context menu for tree nodes in the Tables tab.

  Props:
    :type          \"catalog\" | \"schema\" | \"table\" | \"column\"
    :node          underlying record (live shape: :table-cat/:table-schem/:table-name/:column-name).
                   Sandbox callers normalise to live shape before passing.
    :preview-info  for table rows: `{:type :table :id <qualified> :group-id <cat.schema>}`
                   used by the Preview action to open a preview tab."
  [{:keys [type node preview-info]}]
  (let [sandbox?             @(re-posh/subscribe [:sandbox.subs/enabled?])
        preview-target       @(re-posh/subscribe [:pipeline-editor.subs/preview-target])
        preview-transform-id (when (= "transform" (:type preview-target))
                               (:id preview-target))]
    (case type
      "catalog" [context-menu-item {:on-click (copy-catalog-name node)} "Copy catalog name"]
      "schema"  [context-menu-item {:on-click (copy-schema-name node)} "Copy schema name"]
      "column"  [context-menu-item {:on-click (copy-column-name node)} "Copy column name"]
      "table"   [:<>
                 (when preview-info
                   [context-menu-item {:on-click (preview-handler preview-info)} "Preview"])
                 [context-menu-item {:on-click (copy-table-name node)} "Copy table name"]
                 [context-menu-separator]
                 [context-menu-label "SQL"]
                 [context-menu-item {:on-click (generate-select-handler preview-transform-id node)
                                     :disabled (or (not sandbox?) (nil? preview-transform-id))}
                  "Generate SELECT"]
                 [context-menu-item {:on-click (copy-select-handler node)}
                  "Copy SELECT"]
                 [context-menu-item {:on-click (generate-drop-handler preview-transform-id node)
                                     :disabled (or (not sandbox?) (nil? preview-transform-id))}
                  "Generate DROP TABLE"]
                 [context-menu-item {:on-click (copy-drop-handler node)}
                  "Copy DROP TABLE"]]
      nil)))

(defn transform-context-menu
  "Context menu for transform rows in the Transforms tab.

  Props:
    :master-id  transform master id
    :group-id   transform's group id
    :name       transform display name"
  [{:keys [master-id group-id name]}]
  [:<>
   [context-menu-item {:on-click (preview-handler {:type     :transform
                                                   :id       master-id
                                                   :group-id group-id})}
    "Preview"]
   [context-menu-item {:on-click #(copy! name)}
    "Copy transform name"]])
